/**
 * The home screen's one state, and the small derived facts around it
 * (`01-PRODUCT-SPEC.md` §2.2, `TRACK-D` "Home screen").
 *
 * > The home screen renders exactly one of these. Ambiguity here is a payroll
 * > defect.
 *
 * So this is a pure function with an explicit precedence, table-tested against
 * fixture contexts, rather than a pile of conditions spread across a component.
 *
 * Two rules it never breaks:
 *
 * - `punchability` is **advisory** (`03-API-CONTRACT.md` §2). Nothing here
 *   authorizes a punch; the button always makes a real request and the server
 *   decides. A cached context may be hours stale.
 * - Elapsed time comes from the **server-confirmed** `openedAt` plus the clock
 *   offset — never from a raw device clock.
 */
import type { CurrentShift, MeContextResponse, OpenSession } from '@saniplus/contracts';

import { initialQueueState } from './queueSlice';
import { refusalMessageKey } from './refusals';
import type { FailedItemSummary, QueueItemSummary, QueueState } from './queueSlice';

export type HomeState =
  /** A shift, the window is open, the primary action is POINTER ENTRÉE. */
  | 'NOT_PUNCHED_IN_WINDOW_OPEN'
  /** An open session; the primary action is POINTER SORTIE. */
  | 'PUNCHED_IN'
  /** A shift, but the ±60 min window is not open (or has closed). */
  | 'WINDOW_NOT_OPEN_YET'
  /** Nothing scheduled; show the next one. */
  | 'NO_SHIFT_TODAY'
  /** At least one punch is waiting for the network. */
  | 'OFFLINE_QUEUED'
  /** D-08: an unclosed session blocks a new IN until a supervisor resolves it. */
  | 'BLOCKED_STALE_OPEN'
  /** Transient: a tap is in flight. */
  | 'SUBMITTING'
  /** Transient: the server answered and the time shown is the server's. */
  | 'CONFIRMED';

/** How long the "Pointage enregistré" moment stays on screen by itself. */
export const CONFIRMATION_WINDOW_MS = 8_000;

export interface HomeView {
  readonly state: HomeState;
  readonly shift: CurrentShift | null;
  readonly nextShift: CurrentShift | null;
  readonly openSession: OpenSession | null;
  readonly elapsedMs: number | null;
  readonly windowOpensAt: string | null;
  readonly windowClosesAt: string | null;
  readonly pendingCount: number;
  readonly queuedPunch: QueueItemSummary | null;
  readonly confirmedRecordedAt: string | null;
  /**
   * The i18n key for the sentence under the headline, in this feature's
   * namespace, or `null` when the state needs no explanation. Workers never
   * see a code.
   */
  readonly reasonKey: string | null;
  /** True when the context came off the IndexedDB mirror rather than the API. */
  readonly contextIsCached: boolean;
}

interface HomeStateOptions {
  readonly contextIsCached?: boolean;
}

function view(state: HomeState, parts: Partial<HomeView> = {}): HomeView {
  return {
    state,
    shift: parts.shift ?? null,
    nextShift: parts.nextShift ?? null,
    openSession: parts.openSession ?? null,
    elapsedMs: parts.elapsedMs ?? null,
    windowOpensAt: parts.windowOpensAt ?? null,
    windowClosesAt: parts.windowClosesAt ?? null,
    pendingCount: parts.pendingCount ?? 0,
    queuedPunch: parts.queuedPunch ?? null,
    confirmedRecordedAt: parts.confirmedRecordedAt ?? null,
    reasonKey: parts.reasonKey ?? null,
    contextIsCached: parts.contextIsCached ?? false,
  };
}

/** Elapsed ms since a server-confirmed instant. `null` when nothing is open. */
export function selectElapsed(openedAt: string | null, now: number): number | null {
  if (openedAt === null) return null;
  const started = Date.parse(openedAt);
  if (Number.isNaN(started)) return null;
  return Math.max(0, now - started);
}

/**
 * The single home state.
 *
 * Precedence, highest first — the order *is* the specification:
 *
 * 1. `SUBMITTING` — a tap is in flight; never a silent no-op.
 * 2. `CONFIRMED` — the server answered within the last few seconds.
 * 3. `OFFLINE_QUEUED` — something is waiting; the worker is told before
 *    anything else, because it is the only state where what they see is not
 *    yet what the server knows.
 * 4. `BLOCKED_STALE_OPEN` — D-08's hard block.
 * 5. `PUNCHED_IN` — an open, non-stale session.
 * 6. `NOT_PUNCHED_IN_WINDOW_OPEN` — `canPunchIn`.
 * 7. `WINDOW_NOT_OPEN_YET` — a shift exists, its window does not. The shift
 *    comes from `upcomingShifts[0]`, because the API nulls `currentShift`
 *    precisely when the window is shut.
 * 8. `NO_SHIFT_TODAY` — everything else, including a null context with an
 *    empty queue, where the UI shows its loading/offline shell instead.
 */
export function selectHomeState(
  context: MeContextResponse | null,
  queue: QueueState,
  now: number,
  options: HomeStateOptions = {},
): HomeView {
  const contextIsCached = options.contextIsCached ?? false;
  const pendingCount = queue.pending.length;
  const queuedPunch = queue.pending[queue.pending.length - 1] ?? null;
  const base = { pendingCount, contextIsCached };

  if (queue.submitting.length > 0) {
    return view('SUBMITTING', { ...base, queuedPunch });
  }

  const confirmed = queue.lastConfirmed;
  if (confirmed !== null && now - Date.parse(confirmed.confirmedAt) < CONFIRMATION_WINDOW_MS) {
    return view('CONFIRMED', {
      ...base,
      confirmedRecordedAt: confirmed.recordedAt,
      openSession: context?.openSession ?? null,
      shift: context?.currentShift ?? null,
      elapsedMs: selectElapsed(context?.openSession?.openedAt ?? null, now),
    });
  }

  if (pendingCount > 0) {
    return view('OFFLINE_QUEUED', {
      ...base,
      queuedPunch,
      shift: context?.currentShift ?? null,
      openSession: context?.openSession ?? null,
      reasonKey: 'offline:reason.queued',
    });
  }

  if (context === null) {
    return view('NO_SHIFT_TODAY', base);
  }

  const { punchability, openSession, currentShift, upcomingShifts } = context;
  const nextShift = upcomingShifts[0] ?? null;

  // Amendment #9: `STALE_OPEN_PUNCH` is reported only when the open session is
  // actually stale, by the same code path the real refusal uses.
  if (punchability.reasonCode === 'STALE_OPEN_PUNCH' || openSession?.isStale === true) {
    return view('BLOCKED_STALE_OPEN', {
      ...base,
      openSession,
      shift: currentShift,
      reasonKey: 'offline:reason.staleOpen',
    });
  }

  if (openSession !== null) {
    return view('PUNCHED_IN', {
      ...base,
      openSession,
      shift: currentShift,
      elapsedMs: selectElapsed(openSession.openedAt, now),
    });
  }

  if (punchability.canPunchIn) {
    return view('NOT_PUNCHED_IN_WINDOW_OPEN', {
      ...base,
      shift: currentShift,
      nextShift,
      windowOpensAt: punchability.windowOpensAt,
      windowClosesAt: punchability.windowClosesAt,
    });
  }

  if (punchability.reasonCode === 'OUTSIDE_PUNCH_WINDOW') {
    // One screen, two sentences: the window has not opened, or it has closed
    // (D-08's 60/60 block is hard, and a closed window is the supervisor's
    // `create_missing` escape hatch — D-11 — not a punch the worker can make).
    const opensAt = Date.parse(punchability.windowOpensAt);
    const notYet = Number.isNaN(opensAt) || now < opensAt;
    /*
     * `currentShift` is `selection.punchable` server-side, so it is `null`
     * exactly when the window is shut — the pair
     * (`OUTSIDE_PUNCH_WINDOW`, non-null `currentShift`) never comes off the
     * wire. The shift this screen is about is therefore `upcomingShifts[0]`:
     * a window that has not opened belongs to a shift still in the future. A
     * window that has *closed* belongs to a shift that already started, which
     * the payload no longer names — that screen carries the bounds and no
     * shift. Reading `currentShift` first costs nothing and keeps the
     * selector honest if the server ever does send one.
     */
    const windowShift = currentShift ?? (notYet ? nextShift : null);
    return view('WINDOW_NOT_OPEN_YET', {
      ...base,
      shift: windowShift,
      nextShift,
      windowOpensAt: punchability.windowOpensAt,
      windowClosesAt: punchability.windowClosesAt,
      reasonKey: notYet ? 'offline:reason.windowNotOpenYet' : 'offline:reason.windowClosed',
    });
  }

  return view('NO_SHIFT_TODAY', {
    ...base,
    nextShift,
    shift: currentShift,
    reasonKey: nextShift === null ? 'offline:reason.noShift' : 'offline:reason.nextShift',
  });
}

export type QueueBannerTone = 'syncing' | 'queued' | 'refused' | null;

export interface QueueBanner {
  readonly visible: boolean;
  readonly tone: QueueBannerTone;
  readonly pendingCount: number;
  readonly unacknowledgedFailures: FailedItemSummary[];
  readonly online: boolean;
  readonly syncing: boolean;
  readonly lastSyncAt: string | null;
  readonly messageKey: string | null;
}

/**
 * The persistent header band. `DESIGN.md`: it is shown whenever the queue is
 * non-empty and it never claims a punch is confirmed when it is only queued.
 * A refusal outranks everything — it is the one thing the worker must act on.
 */
export function selectQueueBanner(queue: QueueState): QueueBanner {
  const unacknowledged = queue.failed.filter((item) => !item.acknowledged);
  const pendingCount = queue.pending.length;

  if (unacknowledged.length > 0) {
    const first = unacknowledged[0];
    return {
      visible: true,
      tone: 'refused',
      pendingCount,
      unacknowledgedFailures: unacknowledged,
      online: queue.online,
      syncing: queue.syncing,
      lastSyncAt: queue.lastSyncAt,
      messageKey: first === undefined ? null : refusalMessageKey(first.refusalCode),
    };
  }

  if (pendingCount === 0) {
    return {
      visible: false,
      tone: null,
      pendingCount: 0,
      unacknowledgedFailures: [],
      online: queue.online,
      syncing: queue.syncing,
      lastSyncAt: queue.lastSyncAt,
      messageKey: null,
    };
  }

  return {
    visible: true,
    tone: queue.syncing ? 'syncing' : 'queued',
    pendingCount,
    unacknowledgedFailures: [],
    online: queue.online,
    syncing: queue.syncing,
    lastSyncAt: queue.lastSyncAt,
    messageKey: queue.syncing ? 'offline:queue.syncing' : 'offline:queue.pending',
  };
}

/**
 * The « En attente d'envoi » rule (W4/W5/W6 judgements).
 *
 * The server legitimately reports `missing_punch_out` for a shift whose OUT is
 * still sitting in this device's queue. That flag would tell the worker they
 * forgot to punch out when in fact they did — so the local queue supersedes
 * it, in **one** selector all three screens call rather than three copies that
 * will eventually disagree.
 */
export function selectPendingOutSupersedesMissingPunchOut(
  queue: QueueState,
  shiftId: string | null,
): boolean {
  if (shiftId === null) return false;
  return queue.pending.some((item) => item.type === 'out' && item.shiftId === shiftId);
}

/**
 * Slice accessors, for components that hold the store rather than the mirror.
 *
 * All three are **owner-scoped**, and they are the ONLY readers — the punch
 * screen and the payroll screens each used to keep an unfiltered copy of this
 * with an `initialQueueState` fallback, which is how the highest-stakes screen
 * in the app ended up the one reading the mirror unscoped (B16). The mirror is
 * already cleared on sign-out, so in practice it only ever holds the current
 * session's rows — but a count on a shared phone is exactly the number that
 * must never be someone else's, and a second lock on it costs one comparison.
 * A store with no auth slice (the queue's own unit specs) is read unfiltered.
 *
 * `offlineQueue` is optional for the same reason the deleted copies had a
 * fallback: `app/store.ts` registers the reducer, and a store that has not
 * been wired should render an honest "nothing queued" rather than a white
 * page — IndexedDB is the system of record either way.
 */
interface QueueRootState {
  readonly offlineQueue?: QueueState;
  readonly auth?: { readonly user: { readonly id: string } | null };
}

function sliceOf(state: QueueRootState): QueueState {
  return state.offlineQueue ?? initialQueueState;
}

function ownedBySession<T extends QueueItemSummary>(items: T[], state: QueueRootState): T[] {
  const owner = state.auth?.user?.id;
  if (owner === undefined) return items;
  // A summary with no owner cannot be judged and is left alone: it can only
  // come from a hand-built fixture, never from `summarize()`.
  return items.filter((item) => item.ownerUserId === undefined || item.ownerUserId === owner);
}

export function selectQueueState(state: QueueRootState): QueueState {
  const slice = sliceOf(state);
  const pending = ownedBySession(slice.pending, state);
  const failed = ownedBySession(slice.failed, state);
  if (pending.length === slice.pending.length && failed.length === slice.failed.length) {
    // Identity matters: this feeds `useSelector` and memoized selectors.
    return slice;
  }
  return { ...slice, pending, failed };
}

export function selectPendingCount(state: QueueRootState): number {
  return ownedBySession(sliceOf(state).pending, state).length;
}

export function selectUnacknowledgedFailures(state: QueueRootState): FailedItemSummary[] {
  return ownedBySession(sliceOf(state).failed, state).filter((item) => !item.acknowledged);
}
